import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "../0PageStyles.css";
import "./clientCreationAndEditPage.css";

//Components
import InputField from "../../Components/Other Components/inputField";
import InputGroup from "../../Components/Other Components/inputGroup";
import Button from "../../Components/Other Components/button";
import PopupConfirmation from "../../Components/Other Components/popupConfirmation";
import PageLoader from "../../Components/Other Components/pageLoader";
import MandatoryExplanation from "../../Components/Other Components/mandatoryExplanation";

//Functions and hooks
import {
  GetClientData,
  DeleteClient,
  EditClient,
} from "../../BackendAccess/ClientHooks";

import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import useFormState from "../../hooks/useFormStates";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

const fields = [
  { label: "Client ID", example: "1234", mandatory: true, readOnly: true },
  { label: "Name", example: "Marc Marcus", mandatory: true },
  { label: "Type", example: "Supplier", mandatory: true },
  { label: "Bank", example: "BankName" },
  { label: "Bank Account", example: "6430000000432" },
  { label: "Address", example: "City,Street 1" },
  { label: "Phone", example: "02948291" },
  { label: "Website", example: "www.website.com" },
  { label: "SSN", example: "4392408293402" },
  { label: "TIN", example: "321-543-542-321" },
  { label: "Postal Code", example: "33222" },
];

export default function ClientEditPage() {
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const isNarrow = useNarrowMode();

  const {
    state: { id: currentID },
  } = useLocation();

  const {
    formData: clientData,
    setField,
    loadInitialData,
    hasChanged,
  } = useFormState({});

  useErrorHandler(error, showNotification, setPageError);

  const { t } = useTranslation();
  const { showPopup, removePopup } = usePopup();

  const showConfirmationPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to delete this client?", {
              ns: "clients",
            })}
            <br />{" "}
            {t("All traffic notes attached to this client will be deleted!", {
              ns: "clients",
            })}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => handleDelete(key)}
        onCancel={() => removePopup(key)}
      />,
      key
    );
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [data, error] = await fetchWithTimeout(() =>
          GetClientData(currentID)
        );
        if (error) {
          error.isPageError = true;
          setError(error);
        } else {
          loadInitialData({
            ...data,
            LastEditedBy:
              data.FirstName +
              " " +
              data.LastName +
              " (" +
              data.LastEditedBy +
              ")",
          });
        }
      } catch (err) {
        console.log("CLIENTSSS error", err);

        setError({
          err: t("server.Server error.", { ns: "error" }),
          errCode: 500,
        });
      } finally {
        setLoading(false);
      }
    };

    if (currentID !== undefined && currentID !== null) loadData();
  }, [currentID]);

  const handleChange = (field) => (value) => {
    setField(field, value);
  };

  const handleConfirmChanges = async () => {
    showNotification(t("Editing client...", { ns: "clients" }), "info");
    try {
      const params = {
        currentID,
        ID: clientData.ClientID,
        Name: clientData.Name,
        Type: clientData.Type,
        Bank: clientData.Bank,
        BankAccount: clientData.BankAccount,
        Address: clientData.Address,
        Phone: clientData.Phone,
        Website: clientData.Website,
        SSN: clientData.SSN,
        TIN: clientData.TIN,
        PostalCode: clientData.PostalCode,
        Comment: clientData.Comment,
      };
      const [_, error] = await EditClient(params, currentID);
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Client edited successfully!", { ns: "clients" }),
          "success"
        );
        navigate(-1);
      }
    } catch {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    }
  };

  const handleDelete = async (key) => {
    setDeleteBtnDisabled(true);
    showNotification(t("Deleting client...", { ns: "clients" }), "info");
    try {
      const [_, error] = await DeleteClient(currentID);
      if (error) {
        setError(error);
      } else {
        showNotification(
          t("Client deleted successfully!", { ns: "clients" }),
          "success"
        );
        navigate(-1);
      }
    } catch {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setDeleteBtnDisabled(false);
      removePopup(key);
    }
  };

  const clickOnCancel = () => {
    showNotification(t("Client editing canceled.", { ns: "clients" }), "info");
    navigate(-1);
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("Editing a client", { ns: "tutorial" }),
      message: t(
        "This page shows one client's full details and lets you change or delete it.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-edit-essential",
      title: t("ID, Name, and Type", { ns: "tutorial" }),
      message: t(
        "The ID is fixed and can't be changed. Name is how the client shows up everywhere in the app. Type determines where this client can be picked - as a supplier, manufacturer, or buyer.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-edit-business",
      title: t("Business details", { ns: "tutorial" }),
      message: t(
        "All optional. Bank details, address, contact info, or tax numbers for your records.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-edit-comment",
      title: t("Comment", { ns: "tutorial" }),
      message: t(
        "Optional. A free-text note about this client for your own reference.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-edit-apply-btn",
      title: t("Save your changes", { ns: "tutorial" }),
      message: t(
        "Once you're happy with your changes, click here to save them. This button only lights up once something has actually changed.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "client-edit-delete-btn",
      title: t("Delete", { ns: "tutorial" }),
      message: t(
        "Permanently deletes this client and all traffic notes attached to it. This cannot be undone, so use it carefully.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="clientEdit" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <div className="container-bordered client-creation w-90 mt-3">
            <div className="empty-container client-creation input">
              <InputGroup className="empty-container client-creation regular-fields">
                <div data-tutorial-id="client-edit-essential">
                  {fields.slice(0, 3).map((field, index) => {
                    const key = field.label.replace(/\s/g, "");
                    return (
                      <InputField
                        key={index}
                        placeholder={
                          t("Example") +
                          ": " +
                          t(field.example, { ns: "clients" })
                        }
                        label={t(field.label) + ":"}
                        value={clientData[key] || ""}
                        onChange={handleChange(key)}
                        type={field.label === "Type" ? "dropdown" : ""}
                        options={"Clients"}
                        mandatory={field.mandatory ?? false}
                        readOnly={field.readOnly ?? false}
                      />
                    );
                  })}
                </div>
                <div data-tutorial-id="client-edit-business">
                  {fields.slice(3).map((field, index) => {
                    const key = field.label.replace(/\s/g, "");
                    return (
                      <InputField
                        key={index}
                        placeholder={
                          t("Example") +
                          ": " +
                          t(field.example, { ns: "clients" })
                        }
                        label={t(field.label) + ":"}
                        value={clientData[key] || ""}
                        onChange={handleChange(key)}
                        type={field.label === "Type" ? "dropdown" : ""}
                        options={"Clients"}
                        mandatory={field.mandatory ?? false}
                        readOnly={field.readOnly ?? false}
                      />
                    );
                  })}
                </div>
                {!isNarrow && <MandatoryExplanation />}
              </InputGroup>
              <div className="empty-container client-creation comment-field" data-tutorial-id="client-edit-comment">
                <InputField
                  placeholder={t("Example: This is a very good client.", {
                    ns: "clients",
                  })}
                  label={t("Comment") + ":"}
                  value={clientData.Comment || ""}
                  onChange={handleChange("Comment")}
                  largeField
                  className="mb-1"
                />
                {isNarrow && <MandatoryExplanation />}
              </div>
            </div>
          </div>
          <span className="last-edited-by-bar w-90">
            <span className="sec-textcolor-span">{t("Last edited by:")} </span>
            &nbsp;
            {clientData.LastEditedBy}
          </span>
          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle={"btn-positive"}
              onClick={handleConfirmChanges}
              disabled={!hasChanged}
              acceptEnter={true}
              icon={"apply"}
              tutorialId="client-edit-apply-btn"
            >
              {t("Apply Changes")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-negative"
              onClick={showConfirmationPopup}
              icon={"delete"}
              disabled={deleteBtnDisabled}
              tutorialId="client-edit-delete-btn"
            >
              {t("Delete")}
            </Button>
          </div>
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
